import React, { useState, useContext } from "react";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Divider,
  Alert,
  Badge,
  Tooltip,
  notification,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  KeyOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  UndoOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

// Zambian color scheme
const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  black: "#000000",
  orange: "#FFB81C",
};

const ProfilePage = () => {
  const { user } = useContext(AppContext);
  const isAdmin = (user?.role?.name || user?.role) === "Administrator";
  const [form] = Form.useForm();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState("");
  const token = localStorage.getItem("token");

  const handlePasswordSubmit = async (values) => {
    console.log("Password change form submitted with values:", values);

    setIsChangingPassword(true);
    setPasswordChangeSuccess(false);
    setPasswordChangeError("");

    try {
      const url = `https://zedlenders.pickmesms.com/users/${user.user_id}`;
      const payload = { password: values.newPassword };

      console.log("Attempting to change password via axios API");
      console.log("URL:", url);
      console.log("Payload:", payload);
      console.log("User ID:", user.id);

      const response = await axios.patch(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Password change response:", response);
      setPasswordChangeSuccess(true);
      form.resetFields();

      // Hide success message after 5 seconds
      setTimeout(() => {
        setPasswordChangeSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Error changing password:", error);
      console.error("Error details:", error.response?.data);

      const errorMessage =
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to change password";

      setPasswordChangeError(errorMessage);

      // Hide error message after 5 seconds
      setTimeout(() => {
        setPasswordChangeError("");
      }, 5000);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Test function to debug form submission
  const testFormSubmit = () => {
    console.log("Test button clicked");
    console.log("Form instance:", form);
    console.log("Current form values:", form.getFieldsValue());
    notification.info({
      message: "Debug Test",
      description: "Test function executed - check console",
    });
  };

  return (
    <div className="row">
      <div className="col-md-12">
        <Card bordered={false} style={{ padding: 24 }}>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 24 }}
          >
            <Col>
              <Title
                level={3}
                style={{ margin: 0, color: ZAMBIAN_COLORS.green }}
              >
                <UserOutlined style={{ marginRight: 8 }} />
                User Profile
              </Title>
              <Text type="secondary">
                Manage your account settings and security
              </Text>
            </Col>
          </Row>

          <Row gutter={[24, 24]}>
            {/* Account Information Card - Full Width */}
            <Col xs={24}>
              <Card
                title={
                  <Space>
                    <InfoCircleOutlined
                      style={{ color: ZAMBIAN_COLORS.green }}
                    />
                    Account Information
                  </Space>
                }
              >
                <Row gutter={[32, 24]}>
                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong>Name:</Text>
                      <br />
                      <Text style={{ fontSize: 16 }}>
                        {user?.first_name} {user?.last_name}
                      </Text>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong>Email:</Text>
                      <br />
                      <Text style={{ fontSize: 16 }}>
                        <MailOutlined
                          style={{
                            marginRight: 8,
                            color: ZAMBIAN_COLORS.green,
                          }}
                        />
                        {user?.email}
                      </Text>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong>Role:</Text>
                      <br />
                      <Badge
                        color={ZAMBIAN_COLORS.green}
                        text={user?.role?.name || user?.role}
                        style={{ fontSize: 14 }}
                      />
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <div>
                      <Text strong>Status:</Text>
                      <br />
                      <Badge
                        status={"active"}
                        text="Active"
                        color={ZAMBIAN_COLORS.green}
                        style={{ fontSize: 14 }}
                      />
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Divider />

          {/* Change Password Section */}
          <Card
            title={
              <Space>
                <KeyOutlined style={{ color: ZAMBIAN_COLORS.green }} />
                Change Password
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handlePasswordSubmit}
              size="large"
            >
              <Row gutter={24}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="New Password"
                    autoComplete="new-password"
                    name="newPassword"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your new password",
                      },
                      {
                        min: 8,
                        message: "Password must be at least 8 characters",
                      },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Confirm New Password"
                    name="confirmPassword"
                    autoComplete="new-password"
                    dependencies={["newPassword"]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            getFieldValue("newPassword") === value
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Passwords do not match")
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isChangingPassword}
                    style={{
                      backgroundColor: ZAMBIAN_COLORS.green,
                      borderColor: ZAMBIAN_COLORS.green,
                    }}
                  >
                    {isChangingPassword
                      ? "Changing Password..."
                      : "Change Password"}
                  </Button>

                  <Button
                    type="default"
                    icon={<UndoOutlined />}
                    onClick={() => {
                      form.resetFields();
                      setPasswordChangeSuccess(false);
                      setPasswordChangeError("");
                    }}
                  >
                    Reset Form
                  </Button>

                  {/* <Button
                    type="default"
                    onClick={testFormSubmit}
                    style={{
                      borderColor: ZAMBIAN_COLORS.orange,
                      color: ZAMBIAN_COLORS.orange,
                    }}
                  >
                    Test Debug
                  </Button> */}
                </Space>
              </Form.Item>

              {/* Success/Error Messages */}
              {passwordChangeSuccess && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    backgroundColor: "#f6ffed",
                    border: `1px solid ${ZAMBIAN_COLORS.green}`,
                    borderRadius: 6,
                    color: ZAMBIAN_COLORS.green,
                  }}
                >
                  <Text strong style={{ color: ZAMBIAN_COLORS.green }}>
                    ✓ Password reset was successful!
                  </Text>
                </div>
              )}

              {passwordChangeError && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    backgroundColor: "#fff2f0",
                    border: `1px solid ${ZAMBIAN_COLORS.red}`,
                    borderRadius: 6,
                    color: ZAMBIAN_COLORS.red,
                  }}
                >
                  <Text strong style={{ color: ZAMBIAN_COLORS.red }}>
                    ✗ Error: {passwordChangeError}
                  </Text>
                </div>
              )}
            </Form>
          </Card>

          {/* Security Tips */}
          <Card
            title={
              <Space>
                <Tooltip title="Security Best Practices">
                  <InfoCircleOutlined
                    style={{ color: ZAMBIAN_COLORS.orange }}
                  />
                </Tooltip>
                Password Security Guidelines
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <div style={{ color: "#666", lineHeight: 1.6 }}>
              <Text strong style={{ color: ZAMBIAN_COLORS.orange }}>
                Security Best Practices:
              </Text>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>Use a strong password with at least 8 characters</li>
                <li>
                  Include uppercase, lowercase, numbers, and special characters
                </li>
                <li>Don't reuse passwords from other accounts</li>
                <li>Consider using a password manager</li>
                <li>Enable two-factor authentication when available</li>
              </ul>
            </div>
          </Card>

          {/* Also From Us - Administrators only */}
          {isAdmin && (
            <Card
              title={
                <Space>
                  <AppstoreOutlined style={{ color: ZAMBIAN_COLORS.green }} />
                  Also From Us
                </Space>
              }
              style={{ marginTop: 24 }}
            >
              <Row align="middle" justify="space-between" gutter={[16, 16]}>
                <Col xs={24} md={16}>
                  <Text strong style={{ fontSize: 16 }}>
                    ZedInvoice
                  </Text>
                  <br />
                  <Text type="secondary">
                    Professional invoicing for Zambian businesses — branded
                    ZMW invoices, automated payment reminders, and mobile
                    money collections. Free 7-day trial, then K49/month.
                  </Text>
                </Col>
                <Col xs={24} md={8} style={{ textAlign: "right" }}>
                  <Button
                    type="primary"
                    href="https://zedinvoice.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      backgroundColor: ZAMBIAN_COLORS.green,
                      borderColor: ZAMBIAN_COLORS.green,
                    }}
                  >
                    Visit ZedInvoice
                  </Button>
                </Col>
              </Row>
            </Card>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
